import { useState, useEffect } from "react";
import { MessageCircle, Check, Package, Gift, Sparkles, ArrowLeft, ArrowRight, Leaf, Heart, X, Info, Star, Lock, Calculator, Clock, Bell, Mail, User, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "972528614433";
const KIT_IMAGE = "/images/path-diykit.jpg";

export const DIYKit = () => {
  const { language } = useLanguage();
  const Arrow = language === 'he' ? ArrowLeft : ArrowRight;
  const isRTL = language === 'he';
  const [showLightbox, setShowLightbox] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Waitlist form state
  const [waitlistForm, setWaitlistForm] = useState({ name: "", phone: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState(null); // null | 'registered' | 'already_registered'

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const storedToken = localStorage.getItem('mart_session_token');
      const headers = {};
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include", headers });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setWaitlistForm(prev => ({ ...prev, name: userData.name || "", email: userData.email || "" }));
      }
    } catch (error) { console.log("Not authenticated"); }
    finally { setIsLoading(false); }
  };

  const handleGoogleLogin = () => {
    localStorage.setItem('mart_auth_redirect', '/diy-kit');
    localStorage.setItem('mart_redirect_after_login', '/diy-kit');
    window.location.href = `${BACKEND_URL}/api/auth/login?redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}`;
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name.trim()) return;
    if (!waitlistForm.phone && !waitlistForm.email) return;
    setIsSubmitting(true);
    try {
      const storedToken = localStorage.getItem('mart_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      
      const res = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST', credentials: "include", headers,
        body: JSON.stringify({
          name: waitlistForm.name.trim(),
          phone: waitlistForm.phone.trim() || null,
          email: waitlistForm.email.trim() || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setWaitlistStatus(data.message);
      }
    } catch (error) {
      console.error("Waitlist error:", error);
    } finally { setIsSubmitting(false); }
  };

  const getWhatsAppUrl = () => {
    const message = isRTL
      ? "היי! אשמח להירשם לרשימת ההמתנה לערכה להכנה עצמית של דאודורנט טבעי"
      : "Hi! I'd like to join the waiting list for the DIY natural deodorant kit";
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  // Full ingredient list for kit
  const kitIngredients = {
    he: [
      { name: "שמן קוקוס", percent: "37.5%", weight: "37.5 גרם", desc: "בסיס שומני עיקרי, אנטיבקטריאלי טבעי, נמס במגע עם העור", measure: "2.5 כפות" },
      { name: "חמאת שיאה", percent: "20%", weight: "20 גרם", desc: "מרכך ומייצב, עשירה בוויטמינים A ו-E, מרגיעה עור", measure: "1.3 כפות" },
      { name: "עמילן תירס", percent: "17.5%", weight: "17.5 גרם", desc: "סופח לחות, מעניק תחושת עור יבש ומראה קטיפתי", measure: "1.1 כפות" },
      { name: "סודה לשתייה", percent: "15%", weight: "15 גרם", desc: "מנטרל ריחות, איכות קוסמטית, מינון מופחת למניעת גירוי", measure: "1 כף", quality: "איכות קוסמטית", note: "טווח: 12%-18%" },
      { name: "שעוות דבורים", percent: "5%", weight: "5 גרם", desc: "מייצב טבעי, מרקם רך ונוח למריחה", measure: "1 כפית" },
      { name: "ג׳ל אלוורה", percent: "4.5%", weight: "4.5 גרם", desc: "ריכוך ולחות, מרגיע עור ומאזן את הסודה", measure: "0.9 כפית", note: "אופציונלי, ניתן להחליף בחמאת שיאה" },
      { name: "שמנים אתריים", percent: "0.5%", weight: "0.5 גרם", desc: "ניחוח וחיטוי טבעי, תערובת ארומטית בפיפטות", measure: "~10 טיפות" },
    ],
    en: [
      { name: "Coconut Oil", percent: "37.5%", weight: "37.5g", desc: "Main fat base, natural antibacterial, melts on skin contact", measure: "2.5 tbsp" },
      { name: "Shea Butter", percent: "20%", weight: "20g", desc: "Softener & stabilizer, rich in vitamins A and E, soothes skin", measure: "1.3 tbsp" },
      { name: "Corn Starch", percent: "17.5%", weight: "17.5g", desc: "Absorbs moisture, gives dry skin feel and velvety look", measure: "1.1 tbsp" },
      { name: "Baking Soda", percent: "15%", weight: "15g", desc: "Odor neutralizer, cosmetic grade, reduced dosage to prevent irritation", measure: "1 tbsp", quality: "Cosmetic grade", note: "Range: 12%-18%" },
      { name: "Beeswax", percent: "5%", weight: "5g", desc: "Natural stabilizer, soft texture, easy application", measure: "1 tsp" },
      { name: "Aloe Vera Gel", percent: "4.5%", weight: "4.5g", desc: "Soothing & moisturizing, balances baking soda effect", measure: "0.9 tsp", note: "Optional, can substitute with shea butter" },
      { name: "Essential Oils", percent: "0.5%", weight: "0.5g", desc: "Natural fragrance & disinfection, aromatic blend in pipettes", measure: "~10 drops" },
    ],
  };
  const ingredients = kitIngredients[language] || kitIngredients.he;

  const content = {
    he: {
      title: "ערכה להכנה עצמית",
      subtitle: "כל מה שצריך לחוויית ייצור טבעית ומהנה",
      outOfStock: "כרגע לא במלאי",
      waitlistTitle: "הצטרפו לרשימת ההמתנה",
      waitlistDesc: "השאירו פרטים ונעדכן אתכם ברגע שהערכה תחזור למלאי",
      heroDescription: "ערכה מושלמת להכנת דאודורנט טבעי בבית. כוללת את כל חומרי הגלם האיכותיים, כלי העבודה, הוראות מפורטות ואריזות לאחסון. חוויה טבעית משותפת!",
      kitContents: { title: "מה בערכה?", subtitle: "הכל מוכן לייצור 100 גרם דאודורנט טבעי" },
      loginGate: {
        title: "עוד 5 מרכיבים בערכה", description: "התחברו בחינם עם גוגל כדי לראות את רשימת המרכיבים המלאה",
        buttonText: "התחברות עם גוגל", freeNote: "ההתחברות בחינם לגמרי",
        calculatorNote: "לאחר הצפייה, ניתן להשתמש במחשבון הכמויות לחישוב מדויק", calculatorLink: "למחשבון הכמויות"
      },
      tools: {
        title: "כלים וציוד",
        items: ["מיכל ערבוב עמיד חום ללא רעלים", "מיכל להשרייה במים חמים (בן מארי)", "כלי ערבוב ידני מבמבוק", "מגבת כותנה"],
        note: "ניתן להשתמש גם במיקסר ביתי/חשמלי, רק אם הוא נקי"
      },
      packaging: {
        title: "אריזה ומיתוג",
        items: ["אריזת קרטון מתכלה עם מקום לערבוב", "3 צנצנות זכוכית לאחסון", "תוויות מעוצבות", "מדבקות 'טבעי' למיתוג", "מתנה מאיתנו!"],
        note: "האריזה משמשת גם כמשטח עבודה סטרילי"
      },
      included: {
        title: "מה עוד מקבלים?",
        items: [
          { title: "הוראות מפורטות", description: "פיזי + דיגיטלי, מדריך צעד אחר צעד" },
          { title: "שקיפות מלאה", description: "פירוט עלות כל מרכיב ואריזה" },
          { title: "תמיכה אישית", description: "שאלות? אנחנו בוואטסאפ" }
        ]
      },
      benefits: {
        title: "למה ערכה להכנה עצמית?",
        items: ["חוויה טבעית ומהנה, להכין משהו בידיים", "מספיק לחודשים ואפילו יותר", "אפשר לייצר גם לקרובים כמתנה", "יודעים בדיוק מה על הגוף שלכם"]
      },
      form: { name: "שם מלא", phone: "טלפון", email: "אימייל", submit: "הרשמה לרשימת המתנה", submitting: "שולח...", or: "או" },
      successTitle: "נרשמתם בהצלחה!",
      successMsg: "נעדכן אתכם ברגע שהערכה תחזור למלאי.",
      alreadyMsg: "כבר רשומים! נעדכן אתכם בקרוב.",
      whatsappWaitlist: "הרשמה דרך WhatsApp",
      contactAnytime: "שאלות? פנו אלינו בכל עת",
      alternativeCta: "מעדיפים מוכן?", alternativeText: "יש גם אפשרות לקנות דאודורנט מוכן לשימוש", goToProduct: "לעמוד המוצר"
    },
    en: {
      title: "DIY Kit",
      subtitle: "Everything you need for a natural, enjoyable production experience",
      outOfStock: "Currently out of stock",
      waitlistTitle: "Join the Waiting List",
      waitlistDesc: "Leave your details and we'll notify you when the kit is back in stock",
      heroDescription: "The perfect kit for making natural deodorant at home. Includes all quality raw materials, tools, detailed instructions, and storage containers.",
      kitContents: { title: "What's in the kit?", subtitle: "Everything ready to produce 100g of natural deodorant" },
      loginGate: {
        title: "5 more ingredients in the kit", description: "Sign in for free with Google to see the full ingredients list",
        buttonText: "Sign in with Google", freeNote: "Sign in is completely free",
        calculatorNote: "After viewing, you can use the quantity calculator for precise measurements", calculatorLink: "Go to Calculator"
      },
      tools: {
        title: "Tools & Equipment",
        items: ["Heat-resistant, non-toxic mixing container", "Container for hot water bath (bain-marie)", "Bamboo manual mixing tool", "Cotton towel"],
        note: "You can also use a home/electric mixer, only if it's clean"
      },
      packaging: {
        title: "Packaging & Branding",
        items: ["Biodegradable cardboard packaging", "3 glass jars for storing", "Designed labels", "'Natural' stickers for branding", "A gift from us!"],
        note: "The packaging also serves as a sterile work surface"
      },
      included: {
        title: "What else do you get?",
        items: [
          { title: "Detailed Instructions", description: "Physical + digital, step by step guide" },
          { title: "Full Transparency", description: "Cost breakdown of each ingredient" },
          { title: "Personal Support", description: "Questions? We're on WhatsApp" }
        ]
      },
      benefits: {
        title: "Why a DIY kit?",
        items: ["Natural and enjoyable experience", "Enough for months and more", "Can also make for loved ones as gifts", "Know exactly what's on your body"]
      },
      form: { name: "Full name", phone: "Phone", email: "Email", submit: "Join Waiting List", submitting: "Submitting...", or: "or" },
      successTitle: "You're on the list!",
      successMsg: "We'll let you know when the kit is back in stock.",
      alreadyMsg: "You're already registered! We'll update you soon.",
      whatsappWaitlist: "Join via WhatsApp",
      contactAnytime: "Questions? Reach out anytime",
      alternativeCta: "Prefer ready-made?", alternativeText: "There's also an option to buy ready-to-use deodorant", goToProduct: "Go to Product Page"
    }
  };

  const t = content[language] || content.he;

  return (
    <div className="min-h-screen pt-24 pb-16">
      <SEO page="diy-kit" />
      
      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={() => setShowLightbox(false)}>
            <X className="w-8 h-8" />
          </button>
          <img src={KIT_IMAGE} alt={t.title} className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}
      
      {/* Header */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto text-center">
          {/* Out of Stock Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C47F64]/10 border border-[#C47F64]/30 rounded-full mb-5">
            <Clock className="h-4 w-4 text-[#C47F64]" />
            <span className="text-[#C47F64] text-sm font-semibold">{t.outOfStock}</span>
          </div>
          <h1 data-testid="diy-kit-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C3329] mb-4 animate-fade-in-up">
            {t.title}
          </h1>
          <p className="text-lg text-[#6B7266] animate-fade-in-up animation-delay-200">{t.subtitle}</p>
        </div>
      </section>

      {/* Hero Section with Image and Waitlist */}
      <section className="px-4 sm:px-6 mb-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Kit Image */}
            <div className="image-premium aspect-[4/3] rounded-2xl overflow-hidden animate-fade-in-up cursor-pointer group relative" onClick={() => setShowLightbox(true)}>
              <img src={KIT_IMAGE} alt={t.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid="diy-kit-image" />
              {/* Overlay "out of stock" ribbon */}
              <div className="absolute top-4 right-4 px-4 py-2 bg-[#C47F64] text-white text-sm font-bold rounded-full shadow-md">
                {t.outOfStock}
              </div>
            </div>

            {/* Waitlist Section */}
            <div className="space-y-6 animate-fade-in-up animation-delay-200">
              <p className="text-[#6B7266] leading-relaxed text-lg">{t.heroDescription}</p>

              {/* Key Benefits Quick */}
              <div className="space-y-3">
                {[
                  isRTL ? "כל חומרי הגלם האיכותיים" : "All quality raw materials",
                  isRTL ? "כלי עבודה וציוד" : "Tools and equipment",
                  isRTL ? "אריזות ומיתוג" : "Packaging and branding",
                  isRTL ? "הוראות מפורטות" : "Detailed instructions"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-[#4A5D43]" />
                    <span className="text-[#2C3329]">{item}</span>
                  </div>
                ))}
              </div>

              {/* Waitlist Form Card */}
              <div className="rounded-2xl border-2 border-[#C47F64]/20 bg-gradient-to-br from-[#FDF8F4] to-white p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#C47F64]/15 rounded-full flex items-center justify-center">
                    <Bell className="h-5 w-5 text-[#C47F64]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C3329] font-medium">{t.waitlistTitle}</h3>
                    <p className="text-xs text-[#6B7266]">{t.waitlistDesc}</p>
                  </div>
                </div>

                {waitlistStatus ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 rounded-full bg-[#4A5D43]/10 flex items-center justify-center mx-auto mb-3">
                      <Check className="h-7 w-7 text-[#4A5D43]" />
                    </div>
                    <h4 className="font-serif text-lg text-[#2C3329] mb-2">
                      {waitlistStatus === 'already_registered' ? (isRTL ? 'כבר רשומים!' : 'Already registered!') : t.successTitle}
                    </h4>
                    <p className="text-sm text-[#6B7266]">
                      {waitlistStatus === 'already_registered' ? t.alreadyMsg : t.successMsg}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submitWaitlist} className="space-y-3">
                    <div className="relative">
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                      <input
                        data-testid="waitlist-name-input"
                        type="text"
                        required
                        value={waitlistForm.name}
                        onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })}
                        placeholder={t.form.name}
                        className="w-full pr-10 pl-4 py-3 rounded-xl border border-[#E8E4DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#C47F64]/30 focus:border-[#C47F64] text-sm transition-all"
                      />
                    </div>
                    <div className="relative">
                      <Phone className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                      <input
                        data-testid="waitlist-phone-input"
                        type="tel"
                        value={waitlistForm.phone}
                        onChange={(e) => setWaitlistForm({ ...waitlistForm, phone: e.target.value })}
                        placeholder={t.form.phone}
                        className="w-full pr-10 pl-4 py-3 rounded-xl border border-[#E8E4DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#C47F64]/30 focus:border-[#C47F64] text-sm transition-all"
                        dir="ltr"
                      />
                    </div>
                    <div className="relative">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                      <input
                        data-testid="waitlist-email-input"
                        type="email"
                        value={waitlistForm.email}
                        onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                        placeholder={t.form.email}
                        className="w-full pr-10 pl-4 py-3 rounded-xl border border-[#E8E4DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#C47F64]/30 focus:border-[#C47F64] text-sm transition-all"
                        dir="ltr"
                      />
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] text-center">
                      {isRTL ? 'נדרש טלפון או אימייל (אפשר גם וגם)' : 'Phone or email required (both optional)'}
                    </p>
                    <Button
                      data-testid="waitlist-submit-btn"
                      type="submit"
                      disabled={isSubmitting || !waitlistForm.name.trim() || (!waitlistForm.phone && !waitlistForm.email)}
                      className="w-full bg-[#C47F64] hover:bg-[#b06d53] text-white py-6 rounded-xl text-base font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      <Bell className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isSubmitting ? t.form.submitting : t.form.submit}
                    </Button>
                  </form>
                )}

                {/* WhatsApp alternative */}
                {!waitlistStatus && (
                  <div className="mt-4 pt-4 border-t border-[#E8E4DC]">
                    <p className="text-xs text-[#A3A3A3] text-center mb-2">{t.form.or}</p>
                    <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="block">
                      <Button data-testid="waitlist-whatsapp-btn" variant="outline" className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white py-5 rounded-xl transition-all">
                        <MessageCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t.whatsappWaitlist}
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kit Contents, Premium Ingredient Display */}
      <section className="px-4 sm:px-6 mb-10 py-10 section-warm">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 bg-[#4A5D43]/10 text-[#4A5D43] text-xs font-medium rounded-full mb-4 tracking-wider uppercase">
              {isRTL ? 'חומרי הגלם' : 'Raw Materials'}
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2C3329] mb-2">{t.kitContents.title}</h2>
            <p className="text-[#6B7266]">{t.kitContents.subtitle}</p>
          </div>
          
          {/* First ingredient always visible */}
          <div className="grid gap-px bg-[#E8E4DC] rounded-2xl overflow-hidden shadow-sm">
            <div className="bg-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
              <div className="flex sm:flex-col items-center gap-3 sm:gap-1 sm:w-20 shrink-0">
                <span className="text-2xl sm:text-3xl font-serif font-semibold text-[#2C3329] leading-none">
                  {ingredients[0].percent.replace('%', '')}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-[#9A9790]">%</span>
                <div className="hidden sm:block w-full h-1.5 bg-[#E8E4DC] rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-[#4A5D43] rounded-full" style={{ width: '94%' }} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg text-[#2C3329] font-medium mb-1">{ingredients[0].name}</h3>
                <p className="text-sm text-[#6B7266]">{ingredients[0].desc}</p>
              </div>
              <div className="flex sm:flex-col items-center gap-3 sm:gap-0.5 sm:text-right sm:w-28 shrink-0">
                <span className="font-medium text-[#2C3329] text-sm">{ingredients[0].weight}</span>
                <span className="text-xs text-[#9A9790]">{ingredients[0].measure}</span>
              </div>
            </div>

            {/* Remaining ingredients, login gate for non-authenticated */}
            {!isLoading && (
              <>
                {user ? (
                  <>
                    {ingredients.slice(1).map((item, index) => {
                      const percentNum = parseFloat(item.percent);
                      return (
                        <div key={index} className="bg-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center hover:bg-[#FAF8F5] transition-colors">
                          <div className="flex sm:flex-col items-center gap-3 sm:gap-1 sm:w-20 shrink-0">
                            <span className="text-2xl sm:text-3xl font-serif font-semibold text-[#2C3329] leading-none">
                              {item.percent.replace('%', '')}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-[#9A9790]">%</span>
                            <div className="hidden sm:block w-full h-1.5 bg-[#E8E4DC] rounded-full overflow-hidden mt-1">
                              <div className="h-full bg-[#4A5D43] rounded-full" style={{ width: `${Math.min(percentNum * 2.5, 100)}%` }} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                              <h3 className="font-serif text-lg text-[#2C3329] font-medium">{item.name}</h3>
                              {item.quality && <span className="text-[11px] text-[#4A5D43] bg-[#4A5D43]/8 px-2 py-0.5 rounded font-medium">{item.quality}</span>}
                            </div>
                            <p className="text-sm text-[#6B7266]">{item.desc}</p>
                            {item.note && (
                              <div className="mt-1.5 flex items-center gap-1.5 text-[#C47F64] text-xs font-medium">
                                <Info className="h-3 w-3 shrink-0" />
                                <span>{item.note}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex sm:flex-col items-center gap-3 sm:gap-0.5 sm:text-right sm:w-28 shrink-0">
                            <span className="font-medium text-[#2C3329] text-sm">{item.weight}</span>
                            <span className="text-xs text-[#9A9790]">{item.measure}</span>
                          </div>
                        </div>
                      );
                    })}
                    {/* Total */}
                    <div className="bg-[#4A5D43]/5 p-4 flex items-center justify-between">
                      <span className="font-medium text-[#2C3329] text-sm">{isRTL ? 'סה״כ' : 'Total'}</span>
                      <div className="flex items-center gap-6">
                        <span className="font-serif text-lg font-semibold text-[#4A5D43]">100%</span>
                        <span className="font-medium text-[#2C3329] text-sm">100g</span>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Login Gate overlay */
                  <div className="relative">
                    <div className="divide-y divide-[#E8E4DC]">
                      {[1,2,3,4,5,6].map((_, i) => (
                        <div key={i} className="bg-white/60 p-5 sm:p-6 flex items-center gap-4 blur-[3px]">
                          <div className="w-20 shrink-0"><div className="h-8 bg-gray-200 rounded w-12 mx-auto" /></div>
                          <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-2/3 mb-2" /><div className="h-3 bg-gray-100 rounded w-full" /></div>
                          <div className="w-28 shrink-0"><div className="h-4 bg-gray-200 rounded w-16 ms-auto" /></div>
                        </div>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-white/30 backdrop-blur-[1px]">
                      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl max-w-md mx-4 text-center border border-[#E8E4DC]">
                        <div className="w-14 h-14 rounded-full bg-[#4A5D43]/10 flex items-center justify-center mx-auto mb-4">
                          <Lock className="h-7 w-7 text-[#4A5D43]" />
                        </div>
                        <h3 className="font-serif text-xl text-[#2C3329] mb-2">{t.loginGate.title}</h3>
                        <p className="text-[#6B7266] mb-4 text-sm">{t.loginGate.description}</p>
                        <Button onClick={handleGoogleLogin} className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 py-6 rounded-xl shadow-sm transition-all mb-3" data-testid="google-login-kit-btn">
                          <svg className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          {t.loginGate.buttonText}
                        </Button>
                        <p className="text-xs text-green-600 font-medium">{t.loginGate.freeNote}</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Calculator link for authenticated users */}
          {user && (
            <div className="mt-4 p-4 bg-[#4A5D43]/5 rounded-xl border border-[#4A5D43]/15">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-[#4A5D43]" />
                <div className="flex-1">
                  <p className="text-sm text-[#4A5D43]">{t.loginGate.calculatorNote}</p>
                </div>
                <Link to="/calculator">
                  <Button variant="outline" size="sm" className="text-[#4A5D43] border-[#4A5D43] hover:bg-[#4A5D43] hover:text-white">
                    {t.loginGate.calculatorLink}
                    <Arrow className={`${isRTL ? 'mr-2' : 'ml-2'} h-3 w-3`} />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Tools & Packaging */}
      <section className="px-4 sm:px-6 mb-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="premium-card rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#4A5D43]/10 flex items-center justify-center">
                  <Package className="h-5 w-5 text-[#4A5D43]" />
                </div>
                <h3 className="font-serif text-xl text-[#2C3329]">{t.tools.title}</h3>
              </div>
              <ul className="space-y-3 mb-4">
                {t.tools.items.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-[#4A5D43]" />
                    <span className="text-[#6B7266]">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#9A9790] italic">{t.tools.note}</p>
            </div>
            <div className="premium-card rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Gift className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="font-serif text-xl text-[#2C3329]">{t.packaging.title}</h3>
              </div>
              <ul className="space-y-3 mb-4">
                {t.packaging.items.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-amber-600" />
                    <span className="text-[#6B7266]">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#9A9790] italic">{t.packaging.note}</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Else */}
      <section className="px-4 sm:px-6 mb-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-2xl text-[#2C3329] text-center mb-6">{t.included.title}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {t.included.items.map((item, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-gradient-to-br from-[#F5F2EB] to-white">
                <div className="w-12 h-12 rounded-full bg-[#4A5D43]/10 flex items-center justify-center mx-auto mb-4">
                  {index === 0 && <Sparkles className="h-5 w-5 text-[#4A5D43]" />}
                  {index === 1 && <Star className="h-5 w-5 text-[#4A5D43]" />}
                  {index === 2 && <Heart className="h-5 w-5 text-[#4A5D43]" />}
                </div>
                <h3 className="font-medium text-[#2C3329] mb-2">{item.title}</h3>
                <p className="text-sm text-[#6B7266]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-4 sm:px-6 mb-12 py-8 section-warm">
        <div className="max-w-2xl mx-auto text-center">
          <Leaf className="h-8 w-8 text-[#4A5D43] mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-[#2C3329] mb-6">{t.benefits.title}</h2>
          <div className="space-y-4">
            {t.benefits.items.map((item, index) => (
              <div key={index} className="flex items-center gap-3 justify-center">
                <Check className="h-5 w-5 text-[#4A5D43]" />
                <span className="text-[#6B7266]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Waitlist CTA */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-md mx-auto text-center">
          {!waitlistStatus ? (
            <>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="block mb-3">
                <Button className="w-full bg-[#C47F64] hover:bg-[#b06d53] text-white text-lg py-7 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  <Bell className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} />
                  {t.whatsappWaitlist}
                </Button>
              </a>
              <p className="text-sm text-[#6B7266]">{t.contactAnytime}</p>
            </>
          ) : (
            <div className="flex items-center justify-center gap-2 text-[#4A5D43] font-medium">
              <Check className="h-5 w-5" />
              <span>{t.successMsg}</span>
            </div>
          )}
        </div>
      </section>

      {/* Alternative Path */}
      <section className="px-4 sm:px-6 py-10 section-warm">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-xl text-[#2C3329] mb-3">{t.alternativeCta}</h2>
          <p className="text-[#6B7266] mb-6">{t.alternativeText}</p>
          <Link to="/product">
            <Button variant="outline" className="border-[#4A5D43] text-[#4A5D43] hover:bg-[#4A5D43] hover:text-white rounded-full px-8 py-5">
              {t.goToProduct}
              <Arrow className={`${isRTL ? 'mr-2' : 'ml-2'} h-4 w-4`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DIYKit;
